import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import adminBookingService, { AdminBooking, AdminBookingsResponse, BookingStats } from '../../services/adminBookingService';

interface AdminBookingsState {
  bookings: AdminBooking[];
  currentBooking: AdminBooking | null;
  stats: BookingStats | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
  filters: {
    status?: string;
    email?: string;
    retreatId?: string;
    dateFrom?: string;
    dateTo?: string;
  };
  isLoading: boolean;
  statsLoading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: AdminBookingsState = {
  bookings: [],
  currentBooking: null,
  stats: null,
  pagination: null,
  filters: {},
  isLoading: false,
  statsLoading: false,
  error: null,
  success: false,
};

// Async thunks
export const fetchAllBookings = createAsyncThunk(
  'adminBookings/fetchAllBookings',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await adminBookingService.getAllBookings(page, limit);
      return response.data; // Return the data object containing data array and pagination
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings');
    }
  }
);

export const fetchBookingById = createAsyncThunk(
  'adminBookings/fetchBookingById',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await adminBookingService.getBookingById(bookingId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking');
    }
  }
);

export const updateBookingStatus = createAsyncThunk(
  'adminBookings/updateBookingStatus',
  async ({ bookingId, status }: { bookingId: string; status: string }, { rejectWithValue }) => {
    try {
      const response = await adminBookingService.updateBookingStatus(bookingId, status);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking status');
    }
  }
);

export const cancelAdminBooking = createAsyncThunk(
  'adminBookings/cancelAdminBooking',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await adminBookingService.cancelBooking(bookingId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel booking');
    }
  }
);

export const updateAdminBooking = createAsyncThunk(
  'adminBookings/updateAdminBooking',
  async ({ bookingId, updateData }: { bookingId: string; updateData: Partial<AdminBooking> }, { rejectWithValue }) => {
    try {
      const response = await adminBookingService.updateBooking(bookingId, updateData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking');
    }
  }
);

export const searchBookings = createAsyncThunk(
  'adminBookings/searchBookings',
  async (searchParams: {
    email?: string;
    status?: string;
    retreatId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }, { rejectWithValue }) => {
    try {
      const response = await adminBookingService.searchBookings(searchParams);
      return response.data; // Return the data object containing data array and pagination
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search bookings');
    }
  }
);

export const fetchBookingStats = createAsyncThunk(
  'adminBookings/fetchBookingStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminBookingService.getBookingStats();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking statistics');
    }
  }
);

const adminBookingsSlice = createSlice({
  name: 'adminBookings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentBooking: (state, action: PayloadAction<AdminBooking>) => {
      state.currentBooking = action.payload;
    },
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
    },
    setFilters: (state, action: PayloadAction<Partial<AdminBookingsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all bookings
      .addCase(fetchAllBookings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllBookings.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        // Support both { data, pagination } and a bare array response
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.data)
              ? payload.data.data
              : [];
        state.bookings = list;
        state.pagination = payload?.pagination || payload?.data?.pagination || null;
        state.success = true;
      })
      .addCase(fetchAllBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Search bookings
      .addCase(searchBookings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchBookings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bookings = action.payload.data; // Extract the data array
        state.pagination = action.payload.pagination; // Store pagination info
        state.success = true;
      })
      .addCase(searchBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch booking by ID
      .addCase(fetchBookingById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBooking = action.payload;
        state.success = true;
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update booking status
      .addCase(updateBookingStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        // Update the booking in the list
        const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
        // Update current booking if it's the same one
        if (state.currentBooking?._id === action.payload._id) {
          state.currentBooking = action.payload;
        }
      })
      .addCase(updateBookingStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Cancel booking
      .addCase(cancelAdminBooking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelAdminBooking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        // Update the booking in the list
        const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
        // Update current booking if it's the same one
        if (state.currentBooking?._id === action.payload._id) {
          state.currentBooking = action.payload;
        }
      })
      .addCase(cancelAdminBooking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update booking
      .addCase(updateAdminBooking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAdminBooking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        // Update the booking in the list
        const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
        // Update current booking if it's the same one
        if (state.currentBooking?._id === action.payload._id) {
          state.currentBooking = action.payload;
        }
      })
      .addCase(updateAdminBooking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch booking stats
      .addCase(fetchBookingStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(fetchBookingStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchBookingStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  clearSuccess, 
  setCurrentBooking, 
  clearCurrentBooking, 
  setFilters, 
  clearFilters 
} = adminBookingsSlice.actions;

export default adminBookingsSlice.reducer;